import { useVariants } from "../lib/api";
import type { TopologyPayload } from "../lib/types/topology";


interface SiteStripProps {
  topology: TopologyPayload;
  code?: string;
}


export default function SiteStrip({ topology, code }: SiteStripProps) {
  // Insertion order in layout.cabins is fore-to-aft.
  const seats = Object.values(topology.seats);
  const cabinCounts = new Map<string, number>();
  for (const s of seats) {
    cabinCounts.set(s.cabin, (cabinCounts.get(s.cabin) ?? 0) + 1);
  }
  const orderedCabins = Object.keys(topology.layout.cabins).filter((c) =>
    cabinCounts.has(c),
  );

  // Resolve the URL's ship code to its tail metadata so we can label the
  // aircraft precisely. Unknown codes simply don't render the Tail cell.
  const variants = useVariants().data?.variants ?? [];
  const currentVariant = variants.find(
    (v) => v.variant_id === topology.variant_id,
  );
  const tailMeta = code
    ? currentVariant?.tails?.find((t) => t.code === code)
    : undefined;

  return (
    <div
      className="flex items-center gap-0 border-b border-slate-200 bg-white px-6 shadow-sm"
      style={{ height: 68 }}
    >
      <Stat label="Total" value={seats.length} sub="seats" wide />
      {orderedCabins.map((cab) => (
        <Stat
          key={cab}
          label={cab}
          value={cabinCounts.get(cab)!}
          sub="seats"
        />
      ))}
      <Stat
        label="Fleet"
        value={topology.fleet_label}
        sub={topology.platform}
        wide
        rightAligned={!tailMeta}
      />
      {tailMeta && (
        <Stat
          label="Aircraft"
          value={tailMeta.code}
          sub={tailMeta.tail}
          wide
        />
      )}
    </div>
  );
}


interface StatProps {
  label: string;
  value: string | number;
  sub?: string;
  wide?: boolean;
  rightAligned?: boolean;
}

function Stat({ label, value, sub, wide, rightAligned }: StatProps) {
  return (
    <div
      className={
        "flex flex-col gap-px border-r border-slate-100 px-2 last:border-r-0" +
        (rightAligned ? " ml-auto" : "")
      }
      style={{
        width: wide ? 260 : 130,
        flexShrink: 0,
        flexGrow: 0,
      }}
    >
      <div className="truncate text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={
          "truncate font-bold leading-tight" +
          (wide ? " text-xl" : " text-base text-slate-700")
        }
      >
        {value}
      </div>
      {sub && (
        <div className="truncate text-[10px] text-slate-400">{sub}</div>
      )}
    </div>
  );
}
